import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { Dialog, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import {
  ScanningIndicator,
  ProgressButton,
  ErrorPanel,
  useScanIndicator,
  useToast
} from "@qubit/autoparts";
import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState, MouseEvent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import useFlag from "~/config/flags";
import { AutostoreBin } from "~/features/autostoreBin";
import { AutostoreBinConfigModal } from "~/features/autostoreBinConfigModal/AutostoreBinConfigModal";
import { openBinConfigModal } from "~/features/autostoreBinConfigModal/autostoreBinConfigModal.slice";
import ActionButtons from "~/features/autostorePutaway/ActionButtons";

import ChangeSuggestedBinModal from "~/features/autostorePutaway/modals/ChangeSuggestedBinModal";
import FlagBinModal from "~/features/autostorePutaway/modals/FlagBinModal";
import InventoryHoldModal from "~/features/autostorePutaway/modals/InventoryHoldModal";
import { useGetPutawayTasks } from "~/features/autostorePutaway/useGetPutawayTasks";
import { AutostorePutawayModal } from "~/features/autostorePutawayModal/AutostorePutawayModal";
import { InventoryAddDialog } from "~/features/inventoryAddDialog/InventoryAddDialog";

import { PutawayAdjustModal } from "~/features/putaway/PutawayAdjustModal";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import usePortStatus from "~/hooks/usePortStatus";
import usePrevious from "~/hooks/usePrevProps";
import { useShouldListenToGridEvents } from "~/hooks/useShouldListenToGridEvents";

import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";
import { getBinCompartmentOccupancy } from "~/lib/binCompartmentOccupancy";
import useWindowDimensions from "~/lib/browserDimensions";
import {
  ternaryIff,
  checkIsExpiration,
  getBarcodeValue,
  getAxiosErrorMessage,
  getInventoryDateObj,
  searchProduct,
  runAsyncSafely
} from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGridV2Subscription } from "~/lib/signalr";
import usePromiseInterval from "~/lib/usePromiseIntervalEffect";

import {
  nextEmptyBin,
  fetchPortStatus,
  closeBin,
  setSelectedBinConfigurations,
  resetPortBinData
} from "~/redux/actions";
import {
  setCurrentEmptyBin,
  closeWorkstation
} from "~/redux/actions/autostore";
import { findInventoryByAutostoreBinNumber } from "~/redux/actions/inventory";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import {
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";
import { usePostPutItemAwayMutation } from "~/redux/warehouse/putAwayTasks.hooks";
import { useStartInductionMutation } from "~/redux/warehouse/workstation.hooks";
import {
  AutostoreEvent,
  NextEmptyBinResponse,
  PutAwayTaskSummaryDto,
  SuggestBinConfigurationResponse
} from "~/types/api";

import { AutostorePutawayOldSearch } from "./AutostorePutawayOldSearch";
import { AutostorePutawayOldToolbar } from "./AutostorePutawayOldToolbar";
import { PutawayTasksTableOld } from "./PutawayTasksTableOld";

import {
  setSearchData,
  selectRow,
  setIsPutawayTaskTableRefreshed,
  setOnLeaveInputTextShown,
  setChangeQuantity
} from "./autostorePutawayOld.slice";

type Props = {
  viewTitle: ViewNameTranslation;
};

export const AutostorePutawayOld = (props: Props) => {
  const { viewTitle } = props;
  const selectedAutostoreGridId = useAppSelector(
    selectWorkstationAutostoreGridId
  );
  /** response from nextEmptyBin call */
  const nextEmptyBinByPort = useAppSelector(
    (state) => state.autostore.nextEmptyBinByPort
  );
  const selectedBinConfigurations = useAppSelector(
    (state) => state.autostore.selectedBinConfigurations
  );
  /** portId for independent or parentPortId for multi-port */
  const selectedPortId = useAppSelector(
    (state) => state.workstations.sitePortId
  );
  const portStateByPort = useAppSelector(
    (state) => state.autostore.portStateByPort
  );
  const clientId = useAppSelector(selectUsersClientId);
  const {
    inv_inventoryDateLabel,
    inv_inventoryDateRequired,
    putaway_multipleSearchTerms,
    putaway_showExceptionsButton
  } = useAppSelector(selectServerConfig);
  const siteAllPortIds = useAppSelector(
    (state) => state.workstations.siteAllPortIds
  );
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const suggestedBin = useAppSelector((state) => state.autostore.suggestedBin);
  const currentSelectedBin = useAppSelector(
    (state) => state.autostore.currentEmptyBin
  );

  const currentSelectedPortId = currentSelectedBin?.openBinResponse.portId;
  const currentSelectedBinId = currentSelectedBin?.openBinResponse.binId;

  const [startInduction] = useStartInductionMutation();
  const [putAwayItem] = usePostPutItemAwayMutation();

  const { data: availableBinConfigurations } = useGetBinConfigurationsQuery();

  // Hooks
  const { width } = useWindowDimensions();

  const { t } = useTranslation();
  const { errorToast, successToast } = useToast();
  const shouldListenToGridEvents = useShouldListenToGridEvents();
  useKeyDownHandler();

  const hasPutawayTableV2EnhancedFlag =
    useFlag().tablesV2Enhanced.includes("inductionTableV2");

  const searchAndFilterPutawayTable = useMemo(
    () => <AutostorePutawayOldSearch />,
    []
  );
  const { setMenuItems, setToolbar } = useNavbar({
    centerComponent: !hasPutawayTableV2EnhancedFlag
      ? searchAndFilterPutawayTable
      : undefined,
    viewTitle
  });
  const {
    areAllPortsReady,
    areAllPortsOpen,
    firstPort,
    isSelectedBinReady,
    portStateByPortArray
  } = usePortStatus(portStateByPort, siteAllPortIds, currentSelectedPortId);
  const [scanState, setScanState] = useScanIndicator();
  const prevSuggestedBin = usePrevious<SuggestBinConfigurationResponse | null>(
    suggestedBin
  );

  const dispatch = useAppDispatch();

  const changeQuantity = useAppSelector(
    (state) => state.autostorePutawayOld.changeQuantity
  );
  const isPutawayTaskTableRefreshed = useAppSelector(
    (state) => state.autostorePutawayOld.isPutawayTaskTableRefreshed
  );
  const searchData = useAppSelector(
    (state) => state.autostorePutawayOld.searchData
  );
  const selectedRowId = useAppSelector(
    (state) => state.autostorePutawayOld.selectedRowId
  );

  // Local State - Modals
  const [isPutawayModalOpen, setIsPutawayModalOpen] = useState(false);
  const [changeSuggestedBinModalOpen, setChangeSuggestedBinModalOpen] =
    useState(false);
  const [isAddInventoryPanelOpen, setAddInventoryPanelOpen] = useState(false);
  const [isInventoryHoldModalOpen, setIsInventoryHoldModalOpen] =
    useState(false);
  const [isFlagBinModalOpen, setIsFlagBinModalOpen] = useState(false);

  // Local State
  const [binExceptionsMenuAnchor, setBinExceptionsMenuAnchor] =
    useState<null | HTMLElement>(null);
  const isBinExceptionsMenuOpen = !!binExceptionsMenuAnchor;
  const closeBinExceptionsMenu = () => setBinExceptionsMenuAnchor(null);
  const [binComponentDimensions, setBinComponentDimensions] = useState<
    Record<string, number>
  >({ numOfRows: 1, numOfCols: 1 });

  const pageLimit = 5;
  const {
    putawayTasks,
    putawayTasksLoading,
    error: putawayTasksError
  } = useGetPutawayTasks(pageLimit, searchData);

  const selectedRow = putawayTasks.find(
    (task) => task.putAwayTaskId === selectedRowId
  );
  const [changedQuantity, setChangedQuantity] = useState<number | null>(null);
  const [closePortBtnDisabled, disableClosePortBtn] = useState(true);
  const [inventoryDate, setInventoryDate] = useState<Dayjs | null>(
    dayjs().utc().add(1, "year")
  );
  const [portPollingActive, setPortPollingActive] = useState<boolean>(false);
  // Compartment number (0-3)
  const [selectedCompartment, setSelectedCompartment] = useState<
    number | undefined
  >(undefined);
  // Compartment number (0-3) used for selecting a new compartment
  const [newSelectedCompartment, setNewSelectedCompartment] = useState<
    number | null
  >(null);
  const [binAtPortSeconds, setBinAtPortSeconds] = useState(0);

  const [isPlaceHoldDisabled, setIsPlaceHoldDisabled] = useState(true);
  const [lastScannedBarcode, setLastScannedBarcode] = useState<string | null>(
    null
  );
  const [hasStartInductionRun, setHasStartInductionRun] =
    useState<boolean>(false);

  // Misc
  const workstationId = siteWorkstation?.id;
  const selectedCompartmentBinId =
    !!(selectedCompartment || selectedCompartment === 0) &&
    currentSelectedBin?.autostoreBinCompartments.find(
      (compartment) =>
        compartment.autostoreCompartmentNumber === selectedCompartment + 1
    )?.binId;
  const hasSuggestedBinChanged =
    suggestedBin && suggestedBin !== prevSuggestedBin;
  const browserWidth =
    width < 1280
      ? ternaryIff<"xs" | "sm" | "md">(
          width > 960,
          "md",
          ternaryIff<"xs" | "sm">(width > 600, "sm", "xs")
        )
      : "lg";

  // Action button disabled states
  const shouldShowActionButtons =
    putawayTasksLoading || putawayTasks.length >= 0;
  const isPutawayButtonDisabled =
    !selectedRow ||
    (selectedCompartment && selectedCompartment < 0) ||
    (portPollingActive && !shouldListenToGridEvents) ||
    !isSelectedBinReady;
  const isBinNotEmptyButtonDisabled =
    (portPollingActive && !shouldListenToGridEvents) ||
    !areAllPortsOpen ||
    !areAllPortsReady;

  // Single port only
  const isChangeBinConfigButtonDisabled =
    putawayTasks.length === 0 || !areAllPortsReady || !areAllPortsOpen;
  const isWaitingForBin =
    (portPollingActive && !shouldListenToGridEvents) ||
    (shouldListenToGridEvents &&
      !(
        firstPort?.getPortResponse.mode === "OPEN" &&
        firstPort?.getPortResponse.isReady
      ));
  const singlePortBinId =
    firstPort &&
    ((!portPollingActive && !shouldListenToGridEvents) ||
      (areAllPortsReady && shouldListenToGridEvents))
      ? firstPort.getPortResponse.selectedBin
      : undefined;

  const reset = useCallback(() => {
    dispatch(selectRow(null));
    setNewSelectedCompartment(null);
    setIsPutawayModalOpen(false);
    setSelectedCompartment(undefined);
    setInventoryDate(dayjs().add(1, "year"));
    setLastScannedBarcode(null);
  }, [dispatch]);

  const handleBarcodeScan = (barcode: string) => {
    // if client expects to scan PO and product for one search (search within a search)
    if (putaway_multipleSearchTerms) {
      // if there are already 2 values in the search or the current search has no results, clear and set to new barcode value
      const newBarcodeArray =
        searchData.scannedBarcodes.length > 1 || putawayTasks.length === 0
          ? [barcode]
          : [...searchData.scannedBarcodes, barcode];
      dispatch(
        setSearchData({
          scannedBarcodes: newBarcodeArray,
          offset: 1
        })
      );
    } else {
      dispatch(
        setSearchData({
          scannedBarcodes: [barcode],
          offset: 1
        })
      );
    }
  };

  useBarcodeScanner<boolean>({
    findScanMatch: (buffer: string) => {
      setLastScannedBarcode(buffer);
      const barcodeValue = getBarcodeValue(buffer);
      handleBarcodeScan(barcodeValue);
      setScanState("success");
      return true;
    }
  });

  const putawayTasksErrorMessage = putawayTasksError
    ? getMessageFromRtkError(putawayTasksError)
    : null;
  useEffect(() => {
    if (putawayTasksErrorMessage) {
      errorToast(putawayTasksErrorMessage);
    }
  }, [putawayTasksErrorMessage, errorToast]);

  // Clear port and bin state on first render
  useEffect(() => {
    dispatch(resetPortBinData());
  }, [dispatch]);

  // component did unmount
  useEffect(
    () => () => {
      dispatch(selectRow(null));
      dispatch(
        setSearchData({
          scannedBarcodes: [],
          offset: 1
        })
      );
      reset();
    },
    [dispatch, reset]
  );

  // Set current bin
  useEffect(() => {
    if (
      // if no suggested bin and we don't care about suggested bin, set to first bin
      !suggestedBin &&
      firstPort &&
      nextEmptyBinByPort
    ) {
      dispatch(
        setCurrentEmptyBin(nextEmptyBinByPort[firstPort.getPortResponse.portId])
      );
    } else if (!selectedRow) {
      // after putaway is complete, row will be unselected, unselect bin/port
      dispatch(setCurrentEmptyBin(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextEmptyBinByPort, hasSuggestedBinChanged, firstPort, selectedRow]);

  // After scanning barcode, if there are no putaway tasks returned check backend products.
  // If product doesn't exist display an error.

  useEffect(() => {
    if (putawayTasks.length === 0 && searchData.scannedBarcodes.length) {
      const productSearchFunc = async () => {
        const hits = clientId
          ? await searchProduct(searchData.scannedBarcodes)
          : [];
        if (!hits.length) {
          errorToast(t("scanned product does not exist"));
        }
      };
      void runAsyncSafely(productSearchFunc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putawayTasks.length]);

  const handleSetBinComponentDimensions = () => {
    if (currentSelectedBin?.autostoreBinConfiguration) {
      setBinComponentDimensions({
        numOfRows:
          currentSelectedBin?.autostoreBinConfiguration
            .horizontalCompartmentCount,
        numOfCols:
          currentSelectedBin?.autostoreBinConfiguration.verticalCompartmentCount
      });
    } else {
      setBinComponentDimensions({ numOfRows: 1, numOfCols: 1 });
    }
  };

  const handleFetchPortStatus = async (portId?: number) => {
    await dispatch(fetchPortStatus({ portId }));
  };

  // Port polling: if log publisher enabled, poll port and log publisher state
  const waitInterval = 7;
  usePromiseInterval(
    async () => {
      setBinAtPortSeconds((binAtPortSecondsState) => binAtPortSecondsState + 1);
      if (binAtPortSeconds > waitInterval) {
        await handleFetchPortStatus();
      }
    },
    1000,
    !areAllPortsReady && shouldListenToGridEvents
  );

  // If port polling active but log publisher not active for grid, poll port every half second
  usePromiseInterval(
    async () => {
      await Promise.all(
        (siteWorkstation?.ports ?? []).map((port) =>
          handleFetchPortStatus(port.portId)
        )
      );
    },
    500,
    portPollingActive && !shouldListenToGridEvents
  );

  // Reset binAtPortSeconds, when bin arrives at port
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (areAllPortsReady && binAtPortSeconds > 0) {
      setBinAtPortSeconds(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areAllPortsReady]);

  // Stop polling when bin arrives
  useEffect(() => {
    if (!shouldListenToGridEvents && areAllPortsReady) {
      setPortPollingActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portStateByPort]);

  const handleSetSuggestedCompartment = () => {
    if (currentSelectedBin) {
      const suggestedCompartment =
        suggestedBin?.binNumber &&
        suggestedBin.binCompartmentNumber !== undefined &&
        suggestedBin.binNumber === currentSelectedBin.openBinResponse.binId
          ? suggestedBin.binCompartmentNumber
          : currentSelectedBin.suggestedPutawayCompartment;
      setSelectedCompartment((suggestedCompartment || 1) - 1);
    }
  };

  // Set suggested compartment and bin dimensions
  useEffect(() => {
    if (!currentSelectedBin) {
      return;
    }
    handleSetBinComponentDimensions();
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (newSelectedCompartment === null) {
      handleSetSuggestedCompartment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedBinId]);

  // If there's only one putaway default to that task
  useEffect(() => {
    if (putawayTasks.length === 1) {
      dispatch(selectRow(putawayTasks[0].putAwayTaskId));
    }
  }, [dispatch, putawayTasks]);

  // Set inventory date to display
  useEffect(() => {
    if (checkIsExpiration(inv_inventoryDateLabel)) {
      setInventoryDate(
        selectedRow?.expirationDate ? dayjs(selectedRow.expirationDate) : null
      );
    } else {
      setInventoryDate(
        selectedRow?.manufactureDate ? dayjs(selectedRow.manufactureDate) : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow]);

  // Enable/disable showing text in search bar
  useEffect(() => {
    dispatch(setOnLeaveInputTextShown(!isPutawayTaskTableRefreshed));
  }, [isPutawayTaskTableRefreshed, dispatch]);

  // Clear selected row
  useEffect(() => {
    if (putawayTasks.length === 0 && !putawayTasksLoading) {
      dispatch(selectRow(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [putawayTasks.length]);

  // Enable/disable the 'place inventory hold' button
  useEffect(() => {
    const checkInventoryAvailabilityForPlaceHold = () => {
      const inventoryPromises = [];
      if (selectedAutostoreGridId) {
        for (const port of portStateByPortArray) {
          if (port.getPortResponse.selectedBin === 0) {
            inventoryPromises.push(Promise.resolve(false));
            break;
          }

          inventoryPromises.push(
            dispatch(
              findInventoryByAutostoreBinNumber(
                selectedAutostoreGridId,
                port.getPortResponse.selectedBin
              )
            ).then((response) => response?.length && response.length > 0)
          );
        }
      }
      return Promise.all(inventoryPromises);
    };

    if (portStateByPortArray.length && areAllPortsReady) {
      void runAsyncSafely(async () => {
        const results = await checkInventoryAvailabilityForPlaceHold();
        setIsPlaceHoldDisabled(!results.some((inventory) => inventory));
      });
    } else setIsPlaceHoldDisabled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portStateByPortArray]);

  const gridSub = (data: AutostoreEvent) => {
    if (data.case !== "BinModeChange" || data.event.binMode !== "O") {
      return;
    }

    if (
      data.event.gridId === selectedAutostoreGridId &&
      !!data.event.portId &&
      siteAllPortIds.includes(data.event.portId)
    ) {
      // if "binOpened" event is received, trigger port polling
      setBinAtPortSeconds((time) => time + waitInterval);
    }
  };

  useGridV2Subscription(gridSub);

  // For now, we don't have something like 'close bin' event
  // So there is no way to know when this operation will be completed
  // Because of that, we periodically check if bin is closed
  /** Specific to single port workstation, run through the close port, open port, get next empty bin flow */
  const fetchNextEmptyBin = useCallback(() => {
    const id = setInterval(() => {
      if (!!selectedAutostoreGridId && !!selectedPortId) {
        void runAsyncSafely(async () => {
          const portStatus = await dispatch(fetchPortStatus());
          if (portStatus && portStatus.selectedBin === 0) {
            clearInterval(id);
            dispatch(nextEmptyBin()).catch((error: AxiosError) => {
              errorToast(getAxiosErrorMessage(error));
              if (portStatus.mode === "OPEN") {
                if (!shouldListenToGridEvents) {
                  setPortPollingActive(false);
                }
                if (!closePortBtnDisabled) {
                  disableClosePortBtn(true);
                }
              }
            });
            if (!shouldListenToGridEvents) {
              setPortPollingActive(true);
            }
            reset();
          }
        });
      }
    }, 500);
  }, [
    closePortBtnDisabled,
    dispatch,
    errorToast,
    reset,
    selectedAutostoreGridId,
    selectedPortId,
    shouldListenToGridEvents
  ]);

  // Call close workstation, start induction, and get empty bin
  useEffect(() => {
    if (
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !hasStartInductionRun &&
      selectedBinConfigurations.length === 0 &&
      !!workstationId &&
      !!selectedAutostoreGridId &&
      !!selectedPortId
    ) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setHasStartInductionRun(true);
      void runAsyncSafely(async () => {
        await dispatch(closeWorkstation());
        await runAsyncSafely(
          () =>
            startInduction({
              autostoreGridId: selectedAutostoreGridId,
              workstationId: workstationId
            }),
          {
            onError: (err) => errorToast(getMessageFromRtkError(err)),
            fallback: null //Continue even if startInduction fails
          }
        );

        fetchNextEmptyBin();
      });
    }
  }, [
    selectedAutostoreGridId,
    selectedPortId,
    selectedBinConfigurations,
    hasStartInductionRun,
    workstationId,
    startInduction,
    fetchNextEmptyBin,
    errorToast,
    dispatch
  ]);

  const handleClickBinNotEmptyButton = () => {
    if (currentSelectedBin) {
      Sentry.captureMessage(
        `Bin not empty was clicked on bin number ${currentSelectedBin.openBinResponse.binId}`,
        "info"
      );
      if (shouldListenToGridEvents) {
        setBinAtPortSeconds(0);
      }
      if (currentSelectedBin) {
        setAddInventoryPanelOpen(true);
      }
    }
  };

  const handleClickPlaceInventoryHold = () => {
    closeBinExceptionsMenu();
    setIsInventoryHoldModalOpen((prev) => !prev);
  };

  const handleClickFlagBin = () => {
    closeBinExceptionsMenu();
    setIsFlagBinModalOpen((prev) => !prev);
  };

  const onPutawayButtonClick = async (): Promise<void> => {
    if (!selectedRow || !selectedCompartmentBinId) {
      errorToast("Selected row or compartment bin id is undefined");
      return;
    }
    try {
      const invDate = getInventoryDateObj(
        inv_inventoryDateLabel,
        inventoryDate
      );
      await putAwayItem({
        putAwayTaskId: selectedRow.putAwayTaskId,
        request: {
          quantity: {
            value: changedQuantity || selectedRow.remaining.value,
            units: selectedRow.remaining.units
          },
          targetBinId: selectedCompartmentBinId,
          targetPort: {
            gridId: selectedAutostoreGridId || "",
            portId: currentSelectedPortId || 0
          },
          targetExpiration: invDate.expiration,
          targetManufactureDate: invDate.manufactureDate,
          workstationId
        }
      }).unwrap();

      successToast(t("putaway complete"));

      setIsPutawayModalOpen(false);
      if (putaway_multipleSearchTerms)
        dispatch(setSearchData({ ...searchData, scannedBarcodes: [] }));

      fetchNextEmptyBin();

      // last task on selected page but not a partial putaway - clear search, refresh list
      if (
        putawayTasks.length === 1 &&
        !(changedQuantity && changedQuantity !== selectedRow.remaining.value)
      ) {
        if (searchData.offset !== 1) {
          // user is not on first page, send them back a page
          dispatch(
            setSearchData({ ...searchData, offset: searchData.offset - 1 })
          );
        } else {
          // user is on first page, clear search, then refetch putaways
          if (
            searchData.searchInputValue ||
            searchData.scannedBarcodes.length
          ) {
            dispatch(
              setSearchData({
                scannedBarcodes: [],
                offset: 1
              })
            );
          }
          setIsPutawayTaskTableRefreshed(true);
        }
      }
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
    }
  };

  const onConfirmChangeSuggestedBin = (
    bin: NextEmptyBinResponse,
    compartmentNumber: number
  ) => {
    dispatch(setCurrentEmptyBin(bin));
    setSelectedCompartment(compartmentNumber);
  };

  const inventoryAddPanelRefreshCb = () => {
    if (!currentSelectedBin) {
      return;
    }
    closeBinExceptionsMenu();
    if (currentSelectedBin) {
      void runAsyncSafely(async () => {
        await dispatch(
          closeBin({
            binId: currentSelectedBin.openBinResponse.binId,
            taskId: currentSelectedBin.openBinResponse.taskId
          })
        );
        fetchNextEmptyBin();
      });
    }
  };

  const putawayAdjustModalSuccessCallback = () => {
    dispatch(setChangeQuantity(false));
    dispatch(selectRow(null));
  };

  const handleClickBinExceptionsButton = (
    event: MouseEvent<HTMLButtonElement>
  ) => {
    setBinExceptionsMenuAnchor(event.currentTarget);
  };

  const handleSelectRow = (row: PutAwayTaskSummaryDto) => {
    if (row.putAwayTaskId !== selectedRowId) {
      setNewSelectedCompartment(null);
      dispatch(selectRow(row.putAwayTaskId));
      handleSetSuggestedCompartment();
    }
  };

  useEffect(() => {
    setMenuItems([
      {
        textContent: t("refresh"),
        trackingLabel: "Refresh",
        actionCb: () => {
          dispatch(setIsPutawayTaskTableRefreshed(true));
          setSearchData({
            scannedBarcodes: [],
            offset: 1
          });
          dispatch(selectRow(null));
        }
      }
    ]);
  }, [dispatch, setMenuItems, t]);

  useEffect(() => {
    if (selectedRow && putaway_showExceptionsButton) {
      setToolbar(<AutostorePutawayOldToolbar />);
    } else {
      setToolbar(undefined);
    }
  }, [putaway_showExceptionsButton, selectedRow, setToolbar]);

  const compartmentOccupancy = getBinCompartmentOccupancy(currentSelectedBin);

  return (
    <Box
      sx={{
        p: 2,
        height: {
          xs: "auto",
          md: "calc(100vh - 64px)"
        },
        display: {
          xs: "flex",
          md: "grid"
        },
        flexDirection: "column",
        justifyContent: "center",
        gridTemplateColumns: {
          md: "1fr minmax(200px, 2fr)"
        },
        gridTemplateRows: {
          md: "minmax(auto, 850px) minmax(auto, 200px)"
        },
        gap: {
          xs: 3,
          md: 0
        },
        rowGap: {
          md: 4
        },
        columnGap: {
          md: 2
        }
      }}
    >
      <Stack
        flexDirection="column"
        alignItems="center"
        sx={{
          gridColumn: {
            md: "1/2"
          }
        }}
        gap={4}
      >
        {selectedRow ? (
          <ProductCard.Root
            productName={selectedRow.product.productName}
            imageFileName={selectedRow.product.imageFilename}
            tempZone={selectedRow.product.temperatureZone}
            sx={{ height: "500px" }}
          >
            <ProductCard.Sku sku={selectedRow.product.sku} />
            <ProductCard.ProductCount count={selectedRow.remaining} />
          </ProductCard.Root>
        ) : (
          <ProductCard.Loading marginTop="0" height={490} />
        )}

        {!siteWorkstation && (
          <ErrorPanel m={3} message={t("no autostore port selected")} />
        )}

        {availableBinConfigurations &&
          availableBinConfigurations.length > 1 && (
            <ProgressButton
              style={{
                minWidth: "156px"
              }}
              id="change-bin config-button"
              buttonSize="small"
              emphasis="low"
              responsive
              variant="contained"
              color="primary"
              disabled={isChangeBinConfigButtonDisabled}
              startIcon={<ViewModuleIcon style={{ fontSize: 22 }} />}
              onClick={() => {
                dispatch(openBinConfigModal());
              }}
            >
              {t("change bin config")}
            </ProgressButton>
          )}

        <Stack flexDirection="column" alignItems="center" width="100%">
          {!!singlePortBinId && (
            <Typography
              variant="h5"
              style={{
                textAlign: "center",
                width: "100%"
              }}
            >
              {firstPort?.getPortResponse.selectedBin
                ? `${t("bin")} ${firstPort.getPortResponse.selectedBin}`
                : undefined}
            </Typography>
          )}

          <AutostoreBin
            state={
              !areAllPortsOpen
                ? "Port Closed"
                : isWaitingForBin
                  ? "Waiting for Bin"
                  : "Bin Opened"
            }
            pickQuantity={0}
            pickCompartment={null}
            numberOfRows={binComponentDimensions.numOfRows}
            numberOfColumns={binComponentDimensions.numOfCols}
            binId={singlePortBinId}
            hideBinId
            selectedCompartment={selectedCompartment}
            setSelectedCompartmentCallback={setSelectedCompartment}
            compartmentOccupancy={compartmentOccupancy}
          />
        </Stack>
      </Stack>

      <PutawayTasksTableOld
        inv_inventoryDateLabel={inv_inventoryDateLabel}
        pageLimit={pageLimit}
        selectedRow={selectedRow}
        handleSelectRow={handleSelectRow}
      />

      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          gridRow: {
            md: "2/3"
          },
          gridColumn: {
            md: "1/3"
          }
        }}
      >
        {shouldShowActionButtons && (
          <ActionButtons
            areAllPortsOpen={areAllPortsOpen}
            areAllPortsReady={areAllPortsReady}
            binExceptionsMenuAnchor={binExceptionsMenuAnchor}
            closeBinExceptionsMenu={closeBinExceptionsMenu}
            currentSelectedBin={currentSelectedBin}
            handleClickBinExceptionsButton={handleClickBinExceptionsButton}
            handleClickBinNotEmptyButton={handleClickBinNotEmptyButton}
            handleClickFlagBin={handleClickFlagBin}
            handleClickPlaceInventoryHold={handleClickPlaceInventoryHold}
            isBinExceptionsMenuOpen={isBinExceptionsMenuOpen}
            isBinNotEmptyButtonDisabled={isBinNotEmptyButtonDisabled}
            isPutawayButtonDisabled={isPutawayButtonDisabled}
            shouldListenToGridEvents={shouldListenToGridEvents}
            isPlaceHoldDisabled={isPlaceHoldDisabled}
            portStateByPortArray={portStateByPortArray}
            selectedRow={selectedRow}
            setChangeSuggestedBinModalOpen={setChangeSuggestedBinModalOpen}
            setIsPutawayModalOpen={setIsPutawayModalOpen}
          />
        )}
      </Box>

      {/* Modals */}
      <AutostoreBinConfigModal
        dimensionsCallback={setBinComponentDimensions}
        currentBinConfigurations={selectedBinConfigurations}
        saveBinConfigurations={(configurations) =>
          dispatch(setSelectedBinConfigurations(configurations))
        }
      />
      <AutostorePutawayModal
        inv_inventoryDateRequired={inv_inventoryDateRequired}
        open={isPutawayModalOpen}
        portId={currentSelectedPortId}
        onClose={() => {
          setIsPutawayModalOpen(false);
        }}
        maxWidth={browserWidth}
        browserWidth={width}
        binId={
          currentSelectedBin ? currentSelectedBin.openBinResponse?.binId : null
        }
        onPutawayButtonClick={onPutawayButtonClick}
        changeQuantityFunc={setChangedQuantity}
        inventoryDate={inventoryDate}
        onDateChange={setInventoryDate}
        numberOfRows={binComponentDimensions.numOfRows}
        numberOfColumns={binComponentDimensions.numOfCols}
        selectedCompartment={selectedCompartment}
        compartmentOccupancy={compartmentOccupancy}
        inv_inventoryDateLabel={inv_inventoryDateLabel}
        decantRate={selectedRow?.decantingRate}
        remaining={selectedRow?.remaining}
        isExpirationRequired={selectedRow?.product.isExpirationRequired}
      />
      <InventoryHoldModal
        isOpen={isInventoryHoldModalOpen}
        onClose={() => setIsInventoryHoldModalOpen(false)}
        nextEmptyBinByPort={nextEmptyBinByPort}
        currentSelectedBin={currentSelectedBin}
      />
      <FlagBinModal
        isOpen={isFlagBinModalOpen}
        onClose={() => setIsFlagBinModalOpen(false)}
        nextEmptyBinByPort={nextEmptyBinByPort}
        portStateByPort={portStateByPort}
        workstation={siteWorkstation}
        shouldCloseBinUponSubmit
      />
      {!!currentSelectedBin &&
        (selectedCompartment || selectedCompartment === 0) && (
          <ChangeSuggestedBinModal
            isOpen={changeSuggestedBinModalOpen}
            onClose={() => setChangeSuggestedBinModalOpen(false)}
            nextEmptyBinByPort={nextEmptyBinByPort}
            workstation={siteWorkstation}
            currentSelectedBin={currentSelectedBin}
            onConfirm={onConfirmChangeSuggestedBin}
            currentSelectedCompartment={selectedCompartment}
            newSelectedCompartment={newSelectedCompartment}
            setNewSelectedCompartment={setNewSelectedCompartment}
          />
        )}
      {currentSelectedBin && currentSelectedPortId && (
        <InventoryAddDialog
          open={isAddInventoryPanelOpen}
          onClose={() => {
            setAddInventoryPanelOpen(false);
          }}
          autostorePortId={currentSelectedPortId}
          binId={selectedCompartmentBinId || null}
          refreshCb={inventoryAddPanelRefreshCb}
          disableClosePortOnClose
        />
      )}
      <ScanningIndicator
        scanState={scanState}
        scannedBarcode={lastScannedBarcode}
        placeholderText="Scan Product"
      />
      <Dialog
        open={changeQuantity}
        onClose={() => dispatch(setChangeQuantity(false))}
        aria-labelledby="InventoryAdjustPanel-header-text"
      >
        {changeQuantity && selectedRow && (
          <PutawayAdjustModal
            putawayTask={selectedRow}
            closeModal={() => dispatch(setChangeQuantity(false))}
            successCallback={putawayAdjustModalSuccessCallback}
          />
        )}
      </Dialog>
    </Box>
  );
};
